// Gemeinsame Teile fuer Start und Meine Woche: Aktionen mit Rueckmeldung, Zeile mit Vorschaubild,
// Heute-Karte, Menue eines Eintrags, Tag waehlen, Gericht waehlen.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiFehler, type RezeptKarte, type RezeptKurz, type WochenEintrag } from '../api/client';
import { useRezepte, useWocheHinzufuegen } from '../api/rezepte';
import { useEintragAendern, useEintragEntfernen, useWoche } from '../api/woche';
import {
  kurzesDatum,
  montagVon,
  plusTage,
  tagDerZahl,
  tagName,
  tagVonZeitpunkt,
  wochentagIndex,
  WOCHENTAGE_KURZ,
  langesDatum,
  satz,
  tagImSatz,
} from '../lib/datum';
import { kcalText, passtZurSuche, zeitText } from '../lib/rezept';
import { Icon, IconMehr, Knopf, MenueZeile, RezeptBild, Sheet, Suchfeld, useToast } from '../ui';
import k from '../ui/knoepfe.module.css';
import s from './woche.module.css';

export const FEHLER_SATZ = 'Das hat nicht geklappt. Versuch es gleich noch mal.';

export function metaText(r: { gesamtzeit_min: number | null; kcal: number | null }): string {
  return [zeitText(r.gesamtzeit_min), kcalText(r.kcal)].filter(Boolean).join(' · ');
}

export function rezeptLink(r: { slug: string }): string {
  return `/rezept/${encodeURIComponent(r.slug)}`;
}

/** Zustand eines Eintrags fuer Liste und Kalender. */
export type EintragArt = 'heute' | 'geplant' | 'vorbei' | 'auswahl' | 'gekocht';

export function eintragArt(e: WochenEintrag, heute: string): EintragArt {
  if (e.gekocht_am) return 'gekocht';
  if (e.datum === null || e.datum === undefined) return 'auswahl';
  if (e.datum === heute) return 'heute';
  return e.datum > heute ? 'geplant' : 'vorbei';
}

export function anzahlGerichte(n: number): string {
  return n === 1 ? '1 Gericht' : `${n} Gerichte`;
}

/** Untertitel fuer Start und Meine Woche, an beiden Stellen gleich gezaehlt (QA Runde 2 N7): alles Offene, auch heute. */
export function wocheUnterzeile(eintraege: readonly WochenEintrag[], heute: string): string {
  const offen = eintraege.filter((e) => eintragArt(e, heute) !== 'gekocht');
  const mitTag = offen.filter((e) => eintragArt(e, heute) !== 'auswahl').length;
  if (eintraege.length === 0) return 'Noch keine Gerichte zur Auswahl';
  if (offen.length === 0) return `${anzahlGerichte(eintraege.length)} gekocht, nichts mehr offen`;
  if (mitTag === 0) return `${anzahlGerichte(offen.length)} zur Auswahl, Tage nach Bedarf`;
  return `${anzahlGerichte(offen.length)}, ${mitTag} davon mit Tag`;
}

export function gekochtAm(e: WochenEintrag): string | null {
  return e.datum ?? (e.gekocht_am ? tagVonZeitpunkt(e.gekocht_am) : null);
}

// ---------------------------------------------------------------- Aktionen

/** Alle Aenderungen an der Woche mit Toast und, wo es geht, "Rueckgaengig". */
export function useWochenAktionen(heute: string) {
  const aendern = useEintragAendern();
  const entfernen = useEintragEntfernen();
  const neu = useWocheHinzufuegen();
  const toast = useToast();
  const fehler = () => toast(FEHLER_SATZ);

  const datumSetzen = (e: WochenEintrag, datum: string | null, satz: string, still = false) =>
    aendern.mutate(
      { id: e.id, datum },
      {
        onSuccess: () => {
          if (!still) toast(satz, { label: 'Rückgängig', onClick: () => aendern.mutate({ id: e.id, datum: e.datum ?? null }) });
        },
        onError: fehler,
      },
    );

  return {
    heuteKochen: (e: WochenEintrag) => datumSetzen(e, heute, `Heute gibt es ${e.rezept.name}.`),
    einplanen: (e: WochenEintrag, datum: string) =>
      datumSetzen(e, datum, datum === heute ? `Heute gibt es ${e.rezept.name}.` : satz(`Eingeplant für ${tagImSatz(datum, heute)}`)),
    zurueckInAuswahl: (e: WochenEintrag) => datumSetzen(e, null, `${e.rezept.name} steht wieder zur Auswahl.`),
    /** Tauschen: neues Gericht auf den Tag, das alte (falls noch nicht gekocht) zurueck in die Auswahl. */
    tauschen: (neuerEintrag: WochenEintrag, datum: string, alt: WochenEintrag | undefined) => {
      if (alt && !alt.gekocht_am) datumSetzen(alt, null, '', true);
      datumSetzen(neuerEintrag, datum, datum === heute ? `Heute gibt es ${neuerEintrag.rezept.name}.` : satz(`Eingeplant für ${tagImSatz(datum, heute)}`));
    },
    gekocht: (e: WochenEintrag, wert: boolean) =>
      aendern.mutate(
        { id: e.id, gekocht: wert },
        {
          onSuccess: () =>
            toast(wert ? 'Als gekocht markiert.' : 'Wieder als offen markiert.', {
              label: 'Rückgängig',
              // Gekocht ohne Tag bekommt serverseitig heute; Rueckgaengig stellt auch den alten Tag wieder her
              onClick: () => aendern.mutate({ id: e.id, gekocht: !wert, datum: e.datum ?? null }),
            }),
          onError: fehler,
        },
      ),
    entfernen: (e: WochenEintrag) =>
      entfernen.mutate(e.id, {
        onSuccess: () =>
          toast(`${e.rezept.name} ist aus der Woche.`, {
            label: 'Rückgängig',
            onClick: () => neu.mutate({ rezept_id: e.rezept.id, ...(e.datum ? { datum: e.datum } : {}) }),
          }),
        onError: fehler,
      }),
    /** Mehrfachauswahl (QA Runde 2 N2): alle nacheinander anlegen, eine Meldung, Rueckgaengig nimmt die neuen wieder raus. */
    hinzufuegenMehrere: async (rs: ReadonlyArray<{ id: string; name: string }>) => {
      const neueIds: string[] = [];
      let fehlgeschlagen = 0;
      for (const r of rs) {
        try {
          const x = await neu.mutateAsync({ rezept_id: r.id });
          if (x.ergebnis === 'angelegt') neueIds.push(x.id);
        } catch {
          fehlgeschlagen += 1;
        }
      }
      if (fehlgeschlagen > 0 && neueIds.length === 0) {
        toast(FEHLER_SATZ);
        return;
      }
      const text =
        neueIds.length === 0
          ? 'Die Gerichte sind schon in deiner Woche.'
          : `${anzahlGerichte(neueIds.length)} ${neueIds.length === 1 ? 'liegt' : 'liegen'} jetzt in Meine Woche.` +
            (fehlgeschlagen > 0 ? ` ${anzahlGerichte(fehlgeschlagen)} hat nicht geklappt.` : '');
      toast(
        text,
        neueIds.length === 0
          ? undefined
          : { label: 'Rückgängig', onClick: () => neueIds.forEach((id) => entfernen.mutate(id, { onError: fehler })) },
      );
    },
    hinzufuegen: (r: { id: string; name: string }, datum?: string) =>
      neu.mutate(
        { rezept_id: r.id, ...(datum ? { datum } : {}) },
        {
          onSuccess: (x) => {
            if (x.ergebnis === 'schon_da') toast(datum ? 'Steht für diesen Tag schon auf dem Plan.' : `${r.name} ist schon in deiner Woche.`);
            else if (datum) toast(datum === heute ? `Heute gibt es ${r.name}.` : satz(`Eingeplant für ${tagImSatz(datum, heute)}`));
            else toast(`${r.name} liegt jetzt in Meine Woche.`);
          },
          onError: (f) => toast(f instanceof ApiFehler && f.status === 404 ? 'Dieses Rezept gibt es nicht mehr.' : FEHLER_SATZ),
        },
      ),
  };
}

export type WochenAktionen = ReturnType<typeof useWochenAktionen>;

// ---------------------------------------------------------------- Zeile (Liste)

export function Vorschaubild({ r, klasse, sizes = "100px" }: { r: RezeptKurz; klasse: string | undefined; sizes?: string }) {
  return (
    <Link to={rezeptLink(r)} tabIndex={-1} aria-hidden="true" className={klasse}>
      <RezeptBild bild={r.bild} kategorie={r.kategorie} sizes={sizes} />
    </Link>
  );
}

export function PlanZeile({
  eintrag,
  heute,
  aktionen,
  onMenue,
  onTagWahl,
}: {
  eintrag: WochenEintrag;
  heute: string;
  aktionen: WochenAktionen;
  onMenue: () => void;
  onTagWahl: () => void;
}) {
  const r = eintrag.rezept;
  const art = eintragArt(eintrag, heute);
  return (
    <li className={s.zeile}>
      <Vorschaubild r={r} klasse={s.thumb} />
      <div className={s.zeileInhalt}>
        <div className={s.zeileKopf}>
          <Link to={rezeptLink(r)} className={s.zeileTitel}>
            <span className={s.zeileTitelText}>{r.name}</span>
          </Link>
          <button type="button" className={s.mehr} aria-label={`Weitere Aktionen für ${r.name}`} onClick={onMenue}>
            <IconMehr />
          </button>
        </div>
        {art === 'gekocht' ? (
          <p className={s.gekocht}>
            <Icon name="haken" groesse={16} strich={2.5} />
            Gekocht am {kurzesDatum(gekochtAm(eintrag) ?? heute)}
          </p>
        ) : (
          <>
            <p className={s.meta}>{metaText(r)}</p>
            <div className={s.aktionen}>
              {art === 'heute' && (
                <>
                  <span className={s.pille}>Heute geplant</span>
                  <Link to={rezeptLink(r)} className={`${s.konturKlein} ${s.rechts}`}>
                    Zum Rezept
                  </Link>
                </>
              )}
              {(art === 'geplant' || art === 'vorbei') && eintrag.datum && (
                <>
                  <span className={`${s.pille} ${art === 'vorbei' ? s.pilleVorbei : ''}`}>
                    {art === 'vorbei' ? `War für ${kurzesDatum(eintrag.datum)} geplant` : `Geplant · ${tagName(eintrag.datum, heute)}`}
                  </span>
                  <button type="button" className={`${s.rundKnopf} ${s.rechts}`} aria-label={`Tag für ${r.name} ändern`} onClick={onTagWahl}>
                    <Icon name="woche" groesse={20} />
                  </button>
                </>
              )}
              {art === 'auswahl' && (
                <>
                  <button type="button" className={s.konturKlein} onClick={() => aktionen.heuteKochen(eintrag)}>
                    Heute kochen
                  </button>
                  <button type="button" className={s.rundKnopf} aria-label="Für einen Tag einplanen" onClick={onTagWahl}>
                    <Icon name="wochePlus" groesse={20} />
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------- Heute-Karte

export function HeuteKarte({
  eintrag,
  tauschenLabel,
  onTauschen,
  jetztKochen = false,
}: {
  eintrag: WochenEintrag;
  tauschenLabel: string;
  onTauschen?: () => void;
  /** W8 (Lyra K0): Primaerknopf "Jetzt kochen" statt "Zum Rezept"; das Bild bleibt der Link zum Rezept */
  jetztKochen?: boolean;
}) {
  const r = eintrag.rezept;
  const kochenLink = `/rezept/${r.slug}/kochen?von=%2F${eintrag.portionen ? `&portionen=${eintrag.portionen}` : ''}`;
  return (
    <>
      <Link to={rezeptLink(r)} className={s.hero}>
        <RezeptBild bild={r.bild} kategorie={r.kategorie} bildStatus={r.bild_status} sizes="(max-width: 640px) 100vw, 600px" prioritaet />
        <span className={s.heroLabel}>{r.name}</span>
      </Link>
      <ul className={s.kennzahlen}>
        {zeitText(r.gesamtzeit_min) && (
          <li>
            <Icon name="uhr" groesse={18} />
            {zeitText(r.gesamtzeit_min)}
          </li>
        )}
        {kcalText(r.kcal) && (
          <li>
            <Icon name="flamme" groesse={18} />
            {kcalText(r.kcal)}
          </li>
        )}
        {r.eiweiss_g !== null && r.eiweiss_g !== undefined && (
          <li>
            <Icon name="glas" groesse={18} />
            {Math.round(r.eiweiss_g)} g Eiweiß
          </li>
        )}
      </ul>
      <div className={s.heroKnoepfe}>
        {jetztKochen ? (
          <Link to={kochenLink} className={heroKnopfKlasse}>
            <Icon name="kochen" groesse={20} strich={1.9} />
            Jetzt kochen
          </Link>
        ) : (
          <Link to={rezeptLink(r)} className={heroKnopfKlasse}>
            Zum Rezept
            <Icon name="chevronRechts" groesse={18} strich={2} />
          </Link>
        )}
        {onTauschen && (
          <button type="button" className={`${s.rundKnopf} ${s.rundKnopfGross}`} aria-label={tauschenLabel} onClick={onTauschen}>
            <Icon name="tauschen" groesse={22} />
          </button>
        )}
      </div>
    </>
  );
}

// Primaerknopf als Link: gleiche Optik wie Knopf (GL-023 §9.1), aber mit Link-Element
const heroKnopfKlasse = `${k.knopf} ${k.primaer}`;

// ---------------------------------------------------------------- Menue eines Eintrags

export function EintragMenue({
  eintrag,
  heute,
  aktionen,
  onSchliessen,
  onTagWahl,
}: {
  eintrag: WochenEintrag | null;
  heute: string;
  aktionen: WochenAktionen;
  onSchliessen: () => void;
  onTagWahl: (e: WochenEintrag) => void;
}) {
  const navigate = useNavigate();
  if (!eintrag) return null;
  const art = eintragArt(eintrag, heute);
  const tu = (f: () => void) => () => {
    onSchliessen();
    f();
  };
  return (
    <Sheet offen onSchliessen={onSchliessen} titel={eintrag.rezept.name} unterzeile={unterzeile(eintrag, heute)}>
      {art === 'heute' && (
        <MenueZeile icon="kochen" onClick={tu(() => navigate(`/rezept/${eintrag.rezept.slug}/kochen?von=%2Fwoche${eintrag.portionen ? `&portionen=${eintrag.portionen}` : ''}`))}>
          Jetzt kochen
        </MenueZeile>
      )}
      {art !== 'heute' && art !== 'gekocht' && (
        <MenueZeile icon="kochen" onClick={tu(() => aktionen.heuteKochen(eintrag))}>
          Heute kochen
        </MenueZeile>
      )}
      {art !== 'gekocht' && (
        <MenueZeile icon="wochePlus" onClick={tu(() => onTagWahl(eintrag))}>
          {art === 'auswahl' ? 'Für einen Tag einplanen' : 'Anderen Tag wählen'}
        </MenueZeile>
      )}
      {art !== 'auswahl' && art !== 'gekocht' && (
        <MenueZeile icon="zurueckPool" onClick={tu(() => aktionen.zurueckInAuswahl(eintrag))}>
          Zurück zur Auswahl
        </MenueZeile>
      )}
      {art === 'gekocht' ? (
        <MenueZeile icon="zurueckPool" onClick={tu(() => aktionen.gekocht(eintrag, false))}>
          Doch noch nicht gekocht
        </MenueZeile>
      ) : (
        <MenueZeile icon="haken" onClick={tu(() => aktionen.gekocht(eintrag, true))}>
          Als gekocht markieren
        </MenueZeile>
      )}
      <MenueZeile icon="muell" destruktiv onClick={tu(() => aktionen.entfernen(eintrag))}>
        Aus der Woche nehmen
      </MenueZeile>
    </Sheet>
  );
}

function unterzeile(e: WochenEintrag, heute: string): string {
  const art = eintragArt(e, heute);
  if (art === 'gekocht') return `Gekocht am ${kurzesDatum(gekochtAm(e) ?? heute)}`;
  if (art === 'auswahl') return 'Zur Auswahl, noch ohne Tag';
  if (art === 'heute') return 'Heute geplant';
  return e.datum ? `Geplant für ${langesDatum(e.datum)}` : '';
}

// ---------------------------------------------------------------- Tag waehlen

/** Zwei Wochen ab Montag dieser Woche als Raster, dazu ein Datumsfeld fuer alles Weitere (kuenftige Tage erlaubt). */
export function TagWahl({
  offen,
  titel,
  heute,
  gewaehlt,
  onWahl,
  onSchliessen,
}: {
  offen: boolean;
  titel: string;
  heute: string;
  gewaehlt?: string | null;
  onWahl: (datum: string) => void;
  onSchliessen: () => void;
}) {
  const montag = montagVon(heute);
  const wochen = [0, 1].map((w) => Array.from({ length: 7 }, (_, i) => plusTage(montag, w * 7 + i)));
  return (
    <Sheet offen={offen} onSchliessen={onSchliessen} titel={titel} unterzeile="Auch spätere Tage gehen.">
      {wochen.map((tage, w) => (
        <div key={w}>
          <p className={s.wochenLabel}>{w === 0 ? 'Diese Woche' : 'Nächste Woche'}</p>
          <div className={s.tagRaster}>
            {tage.map((t) => {
              const vorbei = t < heute;
              return (
                <button
                  key={t}
                  type="button"
                  className={`${s.tag} ${t === heute ? s.tagIstHeute : ''}`}
                  aria-pressed={t === gewaehlt}
                  aria-label={`${t === heute ? 'Heute, ' : ''}${langesDatum(t)}`}
                  disabled={vorbei}
                  style={vorbei ? { opacity: 0.4, cursor: 'default' } : undefined}
                  onClick={() => onWahl(t)}
                >
                  <span className={s.tagKurz}>{WOCHENTAGE_KURZ[wochentagIndex(t)]}</span>
                  <span className={s.tagZahl}>{tagDerZahl(t)}</span>
                  <span className={s.tagPunktLeer} />
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <label className={s.datumFeld}>
        Anderes Datum
        <input
          type="date"
          min={heute}
          onChange={(e) => {
            if (e.target.value) onWahl(e.target.value);
          }}
        />
      </label>
    </Sheet>
  );
}

// ---------------------------------------------------------------- Gericht waehlen

/**
 * Gericht fuer einen Tag oder fuer die Woche waehlen: zuerst aus der Auswahl (Pool), darunter alle Rezepte
 * mit Suche. onPool bekommt einen Pool-Eintrag, onRezept ein Rezept, das noch nicht in der Woche ist.
 * Mit onMehrere (QA Runde 2 N2, Woche fuellen): Rezepte antippen markiert sie (runde Checkbox wie Einkaufen),
 * ein Knopf unten legt alle auf einmal in die Woche. Suche und Auswahl beginnen bei jedem Oeffnen leer.
 */
export function GerichtWahl({
  offen,
  titel,
  unterzeile: unter,
  pool,
  onPool,
  onRezept,
  onMehrere,
  onSchliessen,
}: {
  offen: boolean;
  titel: string;
  unterzeile?: string;
  pool: readonly WochenEintrag[];
  onPool?: (e: WochenEintrag) => void;
  onRezept: (r: RezeptKarte) => void;
  onMehrere?: (r: RezeptKarte[]) => void;
  onSchliessen: () => void;
}) {
  const rezepte = useRezepte();
  const woche = useWoche();
  // Schon an einem Tag eingeplant (QA W9 Befund 27): sichtbar machen, Waehlen legt es zusaetzlich auf heute
  const heuteIsoWert = woche.data?.heute ?? '';
  const eingeplant = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of woche.data?.eintraege ?? []) {
      if (!e.gekocht_am && e.datum && e.datum >= heuteIsoWert && !m.has(e.rezept.id)) m.set(e.rezept.id, e.datum);
    }
    return m;
  }, [woche.data, heuteIsoWert]);
  const [suche, setSuche] = useState('');
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  // Beim Oeffnen frisch beginnen (QA Runde 2 N2: der alte Suchbegriff blieb stehen, der neue wurde angehaengt)
  useEffect(() => {
    if (offen) {
      setSuche('');
      setGewaehlt([]);
    }
  }, [offen]);
  const imPool = useMemo(() => new Set(pool.map((p) => p.rezept.id)), [pool]);
  const begriff = suche.trim();
  const mehrfach = onMehrere !== undefined;
  const alle = (rezepte.data ?? [])
    .filter((r) => !((onPool || mehrfach) && imPool.has(r.id)))
    .filter((r) => passtZurSuche(r.suchtext, begriff))
    .sort((a, b) => Number(b.gemerkt) - Number(a.gemerkt) || a.name.localeCompare(b.name, 'de'));
  const sucheRef = useRef<HTMLInputElement>(null);
  const umschalten = (id: string) => {
    const an = !gewaehlt.includes(id);
    setGewaehlt((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]));
    // Einkaufs-App-Muster: nach dem Anhaken aus einer Suche ist das Feld wieder leer und bereit fuers naechste Gericht
    if (an && begriff) {
      setSuche('');
      sucheRef.current?.focus();
    }
  };
  const fuss = mehrfach ? (
    <Knopf
      breit
      icon="wochePlus"
      disabled={gewaehlt.length === 0}
      onClick={() => {
        const nachId = new Map((rezepte.data ?? []).map((r) => [r.id, r]));
        onMehrere(gewaehlt.flatMap((id) => nachId.get(id) ?? []));
      }}
    >
      {gewaehlt.length === 0 ? 'Gerichte antippen' : `${anzahlGerichte(gewaehlt.length)} hinzufügen`}
    </Knopf>
  ) : undefined;
  return (
    <Sheet offen={offen} onSchliessen={onSchliessen} titel={titel} unterzeile={unter} fuss={fuss}>
      {onPool && pool.length > 0 && !begriff && (
        <>
          <p className={s.wochenLabel}>Aus deiner Auswahl</p>
          <ul className={s.wahlListe}>
            {pool.map((p) => (
              <li key={p.id}>
                <button type="button" className={s.wahlZeile} onClick={() => onPool(p)}>
                  <span className={s.thumbKlein}>
                    <RezeptBild bild={p.rezept.bild} kategorie={p.rezept.kategorie} sizes="60px" />
                  </span>
                  <span className={s.wahlText}>
                    <span className={s.wahlName}>{p.rezept.name}</span>
                    <span className={s.wahlMeta}>{metaText(p.rezept)}</span>
                  </span>
                  <Icon name="chevronRechts" groesse={20} strich={2} />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <div className={s.wahlSuche}>
        <Suchfeld
          ref={sucheRef}
          label="Rezept suchen"
          placeholder="Rezept suchen"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          // Antippen markiert den Begriff: der naechste ersetzt ihn, statt angehaengt zu werden
          onFocus={(e) => e.currentTarget.select()}
        />
      </div>
      <p className={s.wochenLabel}>
        {begriff ? `${alle.length} ${alle.length === 1 ? 'Rezept' : 'Rezepte'}` : 'Alle Rezepte'}
        {mehrfach && gewaehlt.length > 0 ? ` · ${gewaehlt.length} ausgewählt` : ''}
      </p>
      <ul className={s.wahlListe}>
        {alle.slice(0, 60).map((r) => {
          const an = gewaehlt.includes(r.id);
          const meta = eingeplant.has(r.id) ? `Eingeplant: ${tagName(eingeplant.get(r.id) ?? '', heuteIsoWert)}` : metaText(r);
          return (
            <li key={r.id}>
              <button
                type="button"
                className={s.wahlZeile}
                {...(mehrfach ? { role: 'checkbox', 'aria-checked': an } : {})}
                onClick={() => (mehrfach ? umschalten(r.id) : onRezept(r))}
              >
                <span className={s.thumbKlein}>
                  <RezeptBild bild={r.bild} kategorie={r.kategorie} sizes="60px" />
                </span>
                <span className={s.wahlText}>
                  <span className={s.wahlName}>{r.name}</span>
                  <span className={s.wahlMeta}>{meta}</span>
                </span>
                {mehrfach ? (
                  <span className={`${s.wahlKreis} ${an ? s.wahlKreisAn : ''}`} aria-hidden="true">
                    {an && <Icon name="haken" groesse={16} strich={3} />}
                  </span>
                ) : (
                  <Icon name="plus" groesse={20} strich={2.2} />
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {rezepte.isError && <p className={s.sheetHinweis}>{FEHLER_SATZ}</p>}
    </Sheet>
  );
}

/** Bestaetigung vor "Woche leeren" (destruktiv, Nora darf das nie, Bernd und Jasmin schon). */
export function LeerenFrage({
  offen,
  titel,
  text,
  knopf,
  onJa,
  onSchliessen,
  children,
  jaGesperrt = false,
}: {
  offen: boolean;
  titel: string;
  text: string;
  knopf: string;
  onJa: () => void;
  onSchliessen: () => void;
  /** Zusatz zwischen Text und Knoepfen (Woche leeren: Schalter fuer die Auswahl) */
  children?: ReactNode;
  jaGesperrt?: boolean;
}) {
  return (
    <Sheet offen={offen} onSchliessen={onSchliessen} titel={titel}>
      <p className={s.sheetHinweis}>{text}</p>
      {children}
      <div className={s.sheetKnoepfe}>
        <Knopf breit icon="muell" onClick={onJa} className={s.destruktivVoll} disabled={jaGesperrt}>
          {knopf}
        </Knopf>
        <Knopf breit art="kontur" onClick={onSchliessen}>
          Abbrechen
        </Knopf>
      </div>
    </Sheet>
  );
}
